import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import apiClient from "../../../../api/apiClient";
import Loading from "../../../../components/Loading/Loading";
import LeadStatusBadge from "./shared/LeadStatusBadge";
import PipelineStageTag from "./shared/PipelineStageTag";
import LeadFormModal from "./modals/LeadFormModal";
import ImportLeadsModal from "./modals/ImportLeadsModal";
import LeadEmailModal from "./modals/LeadEmailModal";
import BulkEmailModal from "./modals/BulkEmailModal";
import LeadGlobeModal from "./modals/LeadGlobeModal";
import TabLockScreen from "./TabLockScreen";
import { CITATION_FILTERS, LEAD_SOURCES, leadContactName, leadDisplayName } from "./shared/crmConstants";
import { PARTNER_TYPES, partnerTypeLabel } from "../../../../constants/supplyChain";

const PRIORITIES = ["", "hot", "warm", "cold"];
const STATUSES = ["", "new", "active", "won", "lost", "converted"];

const LeadList = ({ stages, admins, onSelectLead, userProfile, leadSection }) => {
    const [leads, setLeads] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    // ?source=supply_chain (e.g. from Admin > Supply Chain Disputes) pre-filters the list.
    const [searchParams] = useSearchParams();
    const initialSource = LEAD_SOURCES.some((x) => x.value === searchParams.get("source")) ? searchParams.get("source") : "";
    const [filters, setFilters] = useState({
        status: "", priority: "", source: initialSource, stageId: "", assignedTo: "", citation: "", businessType: "",
    });
    const [sort, setSort] = useState("createdAt");
    const [order, setOrder] = useState("desc");
    const [showForm, setShowForm] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [emailLead, setEmailLead] = useState(null);
    const [phonePopover, setPhonePopover] = useState(null); // { leadId, phone, top, left }
    const [emailPopover, setEmailPopover] = useState(null); // { leadId, email, top, left }
    const [copiedId, setCopiedId] = useState(null);
    const [editLead, setEditLead] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null); // single lead object
    const [deleting, setDeleting] = useState(false);
    // Bulk selection state
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [selectAllFiltered, setSelectAllFiltered] = useState(false);
    const [showBulkEmail, setShowBulkEmail] = useState(false);
    const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [showGlobe, setShowGlobe] = useState(false);
    const phonePopoverRef = useRef(null);
    const emailPopoverRef = useRef(null);
    // Email lock — user must enter passcode once per session before any email action
    const [emailUnlocked, setEmailUnlocked] = useState(false);
    const [pendingEmailAction, setPendingEmailAction] = useState(null); // { type: "single", lead } | { type: "bulk" }
    const LIMIT = 25;
    
    const fetchLeads = useCallback(async (p = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: p, limit: LIMIT, sort, order });
            if (search) params.set("search", search);
            if (leadSection) params.set("leadSection", leadSection);
            Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
            const res = await apiClient.get(`/admin/crm/leads?${params}`);
            setLeads(res.data.leads || []);
            setTotal(res.data.total || 0);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [search, filters, sort, order]);

    // Clear selection when filters/sort change (but NOT on page navigation)
    useEffect(() => {
        setPage(1);
        setSelectedIds(new Set());
        setSelectAllFiltered(false);
        fetchLeads(1);
    }, [filters, sort, order]);

    const handleSearch = (e) => {
        if (e.key === "Enter") {
            setPage(1);
            setSelectedIds(new Set());
            setSelectAllFiltered(false);
            fetchLeads(1);
        }
    };

    const handleSort = (col) => {
        if (sort === col) setOrder((o) => o === "asc" ? "desc" : "asc");
        else { setSort(col); setOrder("asc"); }
    };

    const sortArrow = (col) => sort === col ? (order === "asc" ? " ▲" : " ▼") : "";

    // Close phone popover when clicking outside
    useEffect(() => {
        if (!phonePopover) return;
        const handleOutside = (e) => {
            if (phonePopoverRef.current && !phonePopoverRef.current.contains(e.target)) {
                setPhonePopover(null);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, [phonePopover]);

    const copyToClipboard = (text, key) => {
        navigator.clipboard.writeText(text);
        setCopiedId(key);
        setTimeout(() => setCopiedId((c) => c === key ? null : c), 2000);
    };

    const handlePhoneClick = (e, lead) => {
        e.stopPropagation();
        if (phonePopover?.leadId === lead.id) { setPhonePopover(null); return; }
        const rect = e.currentTarget.getBoundingClientRect();
        setPhonePopover({ leadId: lead.id, phone: lead.phone, top: rect.bottom + 6, left: rect.left + rect.width / 2 });
    };

    const handlePopoverCopy = () => {
        if (!phonePopover) return;
        copyToClipboard(phonePopover.phone, `phone-${phonePopover.leadId}`);
        setTimeout(() => setPhonePopover(null), 800);
    };

    useEffect(() => {
        if (!emailPopover) return;
        const handleOutside = (e) => {
            if (emailPopoverRef.current && !emailPopoverRef.current.contains(e.target)) {
                setEmailPopover(null);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, [emailPopover]);

    const handleEmailClick = (e, lead) => {
        e.stopPropagation();
        if (emailPopover?.leadId === lead.id) { setEmailPopover(null); return; }
        const rect = e.currentTarget.getBoundingClientRect();
        setEmailPopover({ leadId: lead.id, email: lead.email, top: rect.bottom + 6, left: rect.left + rect.width / 2 });
    };

    const handleEmailPopoverCopy = () => {
        if (!emailPopover) return;
        copyToClipboard(emailPopover.email, `email-popover-${emailPopover.leadId}`);
        setTimeout(() => setEmailPopover(null), 800);
    };

    // ── Selection helpers ────────────────────────────────────────────────────
    const pageIds = leads.map((l) => l.id);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
    const somePageSelected = pageIds.some((id) => selectedIds.has(id));
    const selectionActive = selectedIds.size > 0 || selectAllFiltered;
    const selectionCount = selectAllFiltered ? total : selectedIds.size;

    const handleToggleRow = (id) => {
        if (selectAllFiltered) {
            // Switch to manual mode, seed with all current page IDs minus this one
            const next = new Set(pageIds);
            next.delete(id);
            setSelectedIds(next);
            setSelectAllFiltered(false);
            return;
        }
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleToggleAllPage = () => {
        if (selectAllFiltered) {
            setSelectAllFiltered(false);
            setSelectedIds(new Set());
            return;
        }
        if (allPageSelected) {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                pageIds.forEach((id) => next.delete(id));
                return next;
            });
        } else {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                pageIds.forEach((id) => next.add(id));
                return next;
            });
        }
    };

    const handleSelectAllFiltered = () => {
        setSelectAllFiltered(true);
        setSelectedIds(new Set());
    };

    const handleClearSelection = () => {
        setSelectAllFiltered(false);
        setSelectedIds(new Set());
    };

    // ── Single lead delete ───────────────────────────────────────────────────
    const handleDeleteLead = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await apiClient.delete(`/admin/crm/leads/${confirmDelete.id}`);
            setConfirmDelete(null);
            fetchLeads(page);
        } catch (err) {
            alert(err.response?.data?.error || "Failed to delete lead");
        } finally {
            setDeleting(false);
        }
    };

    // ── Bulk delete ──────────────────────────────────────────────────────────
    const handleBulkDelete = async () => {
        setBulkDeleting(true);
        try {
            const payload = selectAllFiltered
                ? { filters: { ...filters, ...(search ? { search } : {}) } }
                : { leadIds: [...selectedIds] };
            await apiClient.delete("/admin/crm/leads/bulk", { data: payload });
            setBulkConfirmDelete(false);
            handleClearSelection();
            setPage(1);
            fetchLeads(1);
        } catch (err) {
            alert(err.response?.data?.error || "Bulk delete failed");
        } finally {
            setBulkDeleting(false);
        }
    };

    const totalPages = Math.ceil(total / LIMIT);
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : "—";
    const fmtVal = (v) => v != null ? `$${Number(v).toLocaleString()}` : "—";

    return (
        <div>
            {/* Toolbar */}
            <div className="crm-filter-bar">
                <input
                    className="admin-search-input"
                    placeholder="Search business, contact, email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearch}
                />
                <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
                    <option value="">All Statuses</option>
                    {STATUSES.filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}>
                    <option value="">All Priorities</option>
                    {PRIORITIES.filter(Boolean).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={filters.stageId} onChange={(e) => setFilters((f) => ({ ...f, stageId: e.target.value }))}>
                    <option value="">All Stages</option>
                    {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select value={filters.assignedTo} onChange={(e) => setFilters((f) => ({ ...f, assignedTo: e.target.value }))}>
                    <option value="">All Reps</option>
                    {admins.map((a) => <option key={a.userId} value={a.userId}>{a.firstName} {a.lastName}</option>)}
                </select>
                <select aria-label="Lead source" value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}>
                    <option value="">All Sources</option>
                    {LEAD_SOURCES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                </select>
                <select aria-label="Business type" value={filters.businessType} onChange={(e) => setFilters((f) => ({ ...f, businessType: e.target.value }))}>
                    <option value="">All Business Types</option>
                    {PARTNER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <select aria-label="Supply-chain citations" value={filters.citation} onChange={(e) => setFilters((f) => ({ ...f, citation: e.target.value }))}>
                    {CITATION_FILTERS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <span className="admin-search-count">{total} lead{total !== 1 ? "s" : ""}</span>
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <button className="admin-btn" onClick={() => setShowImport(true)}>Import CSV</button>
                    <button className="admin-btn" onClick={() => setShowGlobe(true)}>🌍 Lead Map</button>
                    <button className="admin-btn admin-btn--primary" onClick={() => setShowForm(true)}>+ New Lead</button>
                </div>
            </div>

            {/* Select-all filtered banners */}
            {allPageSelected && !selectAllFiltered && total > LIMIT && (
                <div className="crm-select-all-banner">
                    All {leads.length} leads on this page are selected.{" "}
                    <button className="crm-select-all-banner__action" onClick={handleSelectAllFiltered}>
                        Select all {total} leads matching current filters
                    </button>
                </div>
            )}
            {selectAllFiltered && (
                <div className="crm-select-all-banner crm-select-all-banner--active">
                    All {total} leads matching current filters are selected.{" "}
                    <button className="crm-select-all-banner__action" onClick={handleClearSelection}>
                        Clear selection
                    </button>
                </div>
            )}

            {/* Bulk action bar */}
            {selectionActive && (
                <div className="crm-bulk-bar">
                    <span className="crm-bulk-bar__count">
                        {selectionCount} lead{selectionCount !== 1 ? "s" : ""} selected
                    </span>
                    <div className="crm-bulk-bar__actions">
                        <button
                            className="admin-btn admin-btn--primary"
                            onClick={() => {
                                if (emailUnlocked) {
                                    setShowBulkEmail(true);
                                } else {
                                    setPendingEmailAction({ type: "bulk" });
                                }
                            }}
                        >
                            ✉ Email Selected
                        </button>
                        <button className="admin-btn admin-btn--danger" onClick={() => setBulkConfirmDelete(true)}>
                            🗑 Delete Selected
                        </button>
                        <button className="admin-btn" onClick={handleClearSelection}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="admin-loading-wrapper"><Loading /></div>
            ) : leads.length === 0 ? (
                <p className="crm-empty">No leads found.</p>
            ) : (
                <>
                    <div className="admin-table-scroll">
                        <table className="crm-lead-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 36, cursor: "default" }} onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            className="crm-checkbox"
                                            checked={allPageSelected || selectAllFiltered}
                                            ref={(el) => {
                                                if (el) el.indeterminate = !selectAllFiltered && !allPageSelected && somePageSelected;
                                            }}
                                            onChange={handleToggleAllPage}
                                            title={selectAllFiltered ? "Clear selection" : allPageSelected ? "Deselect page" : "Select all on this page"}
                                        />
                                    </th>
                                    <th onClick={() => handleSort("company")}>Business{sortArrow("company")}</th>
                                    <th onClick={() => handleSort("firstName")}>Contact{sortArrow("firstName")}</th>
                                    <th onClick={() => handleSort("activeCitationCount")} title="DYC licenses that report this business as a vendor or customer">
                                        Cited by{sortArrow("activeCitationCount")}
                                    </th>
                                    <th>Stage</th>
                                    <th>Status</th>
                                    <th>Priority</th>
                                    <th>Assigned To</th>
                                    <th onClick={() => handleSort("estimatedValue")}>Est. Value{sortArrow("estimatedValue")}</th>
                                    <th onClick={() => handleSort("createdAt")}>Created{sortArrow("createdAt")}</th>
                                    <th>Contact</th>
                                    <th style={{ width: 80 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map((lead) => (
                                    <tr key={lead.id}>
                                        <td style={{ width: 36 }} onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                className="crm-checkbox"
                                                checked={selectAllFiltered || selectedIds.has(lead.id)}
                                                onChange={() => handleToggleRow(lead.id)}
                                            />
                                        </td>
                                        <td>
                                            <span
                                                className="crm-lead-name-link"
                                                onClick={() => onSelectLead(lead.id)}
                                            >
                                                {leadDisplayName(lead)}
                                            </span>
                                            {lead.businessType && (
                                                <>
                                                    <br />
                                                    <small style={{ color: "var(--color-text-secondary)" }}>{partnerTypeLabel(lead.businessType)}</small>
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            {leadContactName(lead) || <span style={{ color: "var(--color-text-secondary)" }}>—</span>}
                                            {lead.email && (
                                                <>
                                                    <br />
                                                    <small style={{ color: "var(--color-text-secondary)" }}>
                                                        {lead.email}
                                                        <button
                                                            className="crm-copy-inline-btn"
                                                            title="Copy email address"
                                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(lead.email, `email-${lead.id}`); }}
                                                        >
                                                            {copiedId === `email-${lead.id}` ? "✓" : "⎘"}
                                                        </button>
                                                    </small>
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            {lead.activeCitationCount > 0 ? lead.activeCitationCount : "—"}
                                            {lead.unverifiedCitationCount > 0 && lead.activeCitationCount === 0 && (
                                                <span className="crm-priority-badge crm-priority-badge--warm" title="Reported only by registrations awaiting approval" style={{ marginLeft: 4 }}>
                                                    Unverified
                                                </span>
                                            )}
                                            {lead.convertedLicenseNo && (
                                                <span className="crm-priority-badge crm-priority-badge--cold" title={`Now DYC license ${lead.convertedLicenseNo}`} style={{ marginLeft: 4 }}>
                                                    On DYC
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {lead.stageName && (
                                                <PipelineStageTag name={lead.stageName} color={lead.stageColor} />
                                            )}
                                        </td>
                                        <td><LeadStatusBadge status={lead.status} /></td>
                                        <td>
                                            <span className={`crm-priority-badge crm-priority-badge--${lead.priority}`}>
                                                {lead.priority}
                                            </span>
                                        </td>
                                        <td>
                                            {lead.assignedFirstName
                                                ? `${lead.assignedFirstName} ${lead.assignedLastName}`
                                                : <span style={{ color: "var(--color-text-secondary)" }}>Unassigned</span>
                                            }
                                        </td>
                                        <td>{fmtVal(lead.estimatedValue)}</td>
                                        <td>{fmtDate(lead.createdAt)}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <div className="crm-contact-actions">
                                                {lead.phone
                                                    ? (
                                                        <button
                                                            className={`crm-row-action-btn${phonePopover?.leadId === lead.id ? " active" : ""}`}
                                                            title="Show phone number"
                                                            onClick={(e) => handlePhoneClick(e, lead)}
                                                        >
                                                            📞
                                                        </button>
                                                    )
                                                    : <span className="crm-contact-dash">—</span>
                                                }
                                                {lead.email && (
                                                    <button
                                                        className={`crm-row-action-btn${emailPopover?.leadId === lead.id ? " active" : ""}`}
                                                        title="Show email address"
                                                        onClick={(e) => handleEmailClick(e, lead)}
                                                    >
                                                        ✉
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ width: 80 }} onClick={(e) => e.stopPropagation()}>
                                            <button
                                                className="crm-row-edit-btn"
                                                title="Edit lead"
                                                onClick={(e) => { e.stopPropagation(); setEditLead(lead); }}
                                            >
                                                ✏ Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="admin-pagination">
                            <button className="admin-page-btn" disabled={page === 1} onClick={() => { setPage(page - 1); fetchLeads(page - 1); }}>
                                ← Prev
                            </button>
                            <span className="admin-page-info">Page {page} of {totalPages}</span>
                            <button className="admin-page-btn" disabled={page === totalPages} onClick={() => { setPage(page + 1); fetchLeads(page + 1); }}>
                                Next →
                            </button>
                        </div>
                    )}
                </>
            )}

            {showForm && (
                <LeadFormModal
                    stages={stages}
                    admins={admins}
                    leadSection={leadSection}
                    onClose={() => setShowForm(false)}
                    onSaved={() => { setShowForm(false); fetchLeads(page); }}
                />
            )}
            {editLead && (
                <LeadFormModal
                    lead={editLead}
                    stages={stages}
                    admins={admins}
                    onClose={() => setEditLead(null)}
                    onSaved={() => { setEditLead(null); fetchLeads(page); }}
                />
            )}
            {showImport && (
                <ImportLeadsModal
                    onClose={() => setShowImport(false)}
                    onImported={() => fetchLeads(1)}
                />
            )}
            {/* Email passcode gate — shown whenever an email action is triggered but not yet unlocked */}
            {pendingEmailAction && !emailUnlocked && (
                <TabLockScreen
                    modal
                    tabLabel="Email"
                    onUnlock={() => {
                        setEmailUnlocked(true);
                        // Open the intended email modal immediately after unlocking
                        if (pendingEmailAction.type === "single") {
                            setEmailLead(pendingEmailAction.lead);
                        } else {
                            setShowBulkEmail(true);
                        }
                        setPendingEmailAction(null);
                    }}
                    onClose={() => setPendingEmailAction(null)}
                />
            )}

            {emailLead && (
                <LeadEmailModal
                    lead={emailLead}
                    onClose={() => setEmailLead(null)}
                    onSent={() => setEmailLead(null)}
                />
            )}

            {/* Single lead delete confirmation */}
            {confirmDelete && (
                <div className="crm-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
                    <div className="crm-modal" style={{ maxWidth: 440 }}>
                        <div className="crm-modal__header">
                            <h2 className="crm-modal__title">Delete Lead</h2>
                            <button className="crm-modal__close" onClick={() => setConfirmDelete(null)}>×</button>
                        </div>
                        <div className="crm-modal__body">
                            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
                                Permanently delete <strong>{leadDisplayName(confirmDelete)}</strong>?
                                This will remove the lead and all associated activities. <strong>This cannot be undone.</strong>
                            </p>
                        </div>
                        <div className="crm-modal__footer">
                            <button className="admin-btn" onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</button>
                            <button className="admin-btn admin-btn--danger" onClick={handleDeleteLead} disabled={deleting}>
                                {deleting ? "Deleting…" : "Delete Permanently"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk email modal */}
            {showBulkEmail && (
                <BulkEmailModal
                    selectedIds={selectedIds}
                    selectAllFiltered={selectAllFiltered}
                    filters={filters}
                    search={search}
                    recipientCount={selectionCount}
                    onClose={() => setShowBulkEmail(false)}
                    onSent={() => { setShowBulkEmail(false); handleClearSelection(); }}
                />
            )}

            {showGlobe && (
                <LeadGlobeModal
                    onClose={() => setShowGlobe(false)}
                    onSelectLead={onSelectLead}
                />
            )}

            {/* Bulk delete confirmation */}
            {bulkConfirmDelete && (
                <div className="crm-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setBulkConfirmDelete(false); }}>
                    <div className="crm-modal" style={{ maxWidth: 440 }}>
                        <div className="crm-modal__header">
                            <h2 className="crm-modal__title">Delete {selectionCount} Lead{selectionCount !== 1 ? "s" : ""}</h2>
                            <button className="crm-modal__close" onClick={() => setBulkConfirmDelete(false)}>×</button>
                        </div>
                        <div className="crm-modal__body">
                            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
                                Permanently delete <strong>{selectionCount} lead{selectionCount !== 1 ? "s" : ""}</strong>
                                {selectAllFiltered ? " matching current filters" : ""}?
                                This removes all selected leads and their associated activities.{" "}
                                <strong>This cannot be undone.</strong>
                            </p>
                        </div>
                        <div className="crm-modal__footer">
                            <button className="admin-btn" onClick={() => setBulkConfirmDelete(false)} disabled={bulkDeleting}>Cancel</button>
                            <button className="admin-btn admin-btn--danger" onClick={handleBulkDelete} disabled={bulkDeleting}>
                                {bulkDeleting ? "Deleting…" : `Delete ${selectionCount} Lead${selectionCount !== 1 ? "s" : ""}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Fixed-position phone popover — renders outside table so it's never clipped */}
            {phonePopover && (
                <div
                    ref={phonePopoverRef}
                    className="crm-phone-popover"
                    style={{ top: phonePopover.top, left: phonePopover.left }}
                    onClick={handlePopoverCopy}
                    title="Click to copy"
                >
                    <span className="crm-phone-popover-icon">📞</span>
                    <span className="crm-phone-popover-number">
                        {copiedId === `phone-${phonePopover.leadId}` ? "✓ Copied!" : phonePopover.phone}
                    </span>
                </div>
            )}
            {emailPopover && (
                <div
                    ref={emailPopoverRef}
                    className="crm-phone-popover"
                    style={{ top: emailPopover.top, left: emailPopover.left }}
                    onClick={handleEmailPopoverCopy}
                    title="Click to copy"
                >
                    <span className="crm-phone-popover-icon">✉</span>
                    <span className="crm-phone-popover-number">
                        {copiedId === `email-popover-${emailPopover.leadId}` ? "✓ Copied!" : emailPopover.email}
                    </span>
                </div>
            )}
        </div>
    );
};

export default LeadList;
